import { html, nothing } from 'lit';
import { SdsSelect } from './sds-select.lit';
import { cleanMenuItemsFocus, initMenuItemsFocus, scrollToSelected, } from '../menu-items-state-handler';
import { SdsInputSelect } from '../../input/select/sds-input-select.lit';
import { translate } from '../../utilities/translate';
import { SdsContextMenuSkeletonItem } from '../../context-menu/item/sds-cm-skeleton-item.lit';
export class SelectRenderer {
    constructor(sdsSelect) {
        this._wasLoading = false;
        this.handleInput = (event) => {
            this.handleInputChanged(event);
            this._sdsSelect.dispatchInputEvent();
            if (this._sdsSelect.selectInputValue !== '') {
                this.openMenu();
            }
        };
        this.handleInputChanged = (event) => {
            this._sdsSelect.selectInputValue = event.detail.value.toString();
            this._sdsSelect.filter = this._sdsSelect.selectInputValue;
            this._sdsSelect.selectItemStateHandler(item => {
                item.focused = false;
            });
            this.resetScroll();
            event.stopPropagation();
            event.preventDefault();
        };
        this.calculateMenuSize = () => {
            const inputWidth = this._sdsSelect.getBoundingClientRect().width;
            return `${inputWidth}px`;
        };
        this.handleClear = () => {
            this._sdsSelect.selectItemStateHandler(item => item.setSelectedSilently(false));
            if (this._sdsSelect.selectedItemIdentifier !== '') {
                this.dispatchClearEvent(this._sdsSelect.selectedItemIdentifier);
                this._sdsSelect.dispatchSelectEvent('');
                this._sdsSelect.dispatchChangeEvent('');
            }
            this.resetValue();
            this._sdsSelect.focused = true;
            this.resetScroll();
            initMenuItemsFocus(this._sdsSelect.contextMenu);
        };
        this.handleCaret = () => {
            if (this._sdsSelect.contextMenu) {
                if (this._sdsSelect.contextMenu.opened) {
                    this.hideMenu();
                }
                else {
                    this.openMenu();
                    this._sdsSelect.inputComponent.setCursorToEnd();
                }
            }
        };
        this.openMenu = () => {
            const target = this._sdsSelect.trigger;
            if (target instanceof SdsInputSelect &&
                this._sdsSelect.contextMenu &&
                !this._sdsSelect.contextMenu.opened) {
                const trigger = target.getInput();
                this._sdsSelect.contextMenu.width = this.calculateMenuSize();
                this._sdsSelect.contextMenu.show(trigger);
                this._sdsSelect.inputComponent.opened = true;
                this._sdsSelect.focused = true;
                this._sdsSelect.addEventListeners();
                initMenuItemsFocus(this._sdsSelect.contextMenu);
                // Set initial ariaActiveDescendantElement to the focused item
                setTimeout(() => {
                    scrollToSelected(this._sdsSelect.contextMenu);
                    this._sdsSelect.setInitialAriaActiveDescendant();
                });
                this._sdsSelect.dispatchMenuOpenEvent();
            }
        };
        this.handleKeyDown = (event) => {
            var _a;
            if (!this._sdsSelect.disabled &&
                (event.code === 'Space' || event.code === 'ArrowDown') &&
                !((_a = this._sdsSelect.contextMenu) === null || _a === void 0 ? void 0 : _a.opened)) {
                event.preventDefault();
                event.stopPropagation();
                this.openMenu();
            }
        };
        this._sdsSelect = sdsSelect;
    }
    render() {
        var _a;
        const noData = this._sdsSelect.selectItems.length === 0 ||
            this._sdsSelect.selectItems.every(item => item.style.display === 'none');
        const isExpandedValue = ((_a = this._sdsSelect.inputComponent) === null || _a === void 0 ? void 0 : _a.opened) || false;
        return html `
            <div class="sds-select-container">
                <sds-input-select
                    id="trigger"
                    .placeholder="${this._sdsSelect.placeholder}"
                    .value="${this._sdsSelect.selectInputValue}"
                    .hint="${this._sdsSelect.hint}"
                    .errorMessage="${this._sdsSelect.errorMessage}"
                    .isExpanded="${isExpandedValue}"
                    searchable
                    ?invalid="${this._sdsSelect.invalid}"
                    ?disabled="${this._sdsSelect.disabled}"
                    ?focused="${this._sdsSelect.focused}"
                    ?pagination="${this._sdsSelect.pagination}"
                    @keydown="${this.handleKeyDown}"
                    @sds.input="${this.handleInput}"
                    @sds.input.select.cleared="${this.handleClear}"
                    @sds.input.select.caret.changed="${this.handleCaret}"
                    @sds.input.select.input.clicked="${this.openMenu}"
                ></sds-input-select>

                <sds-context-menu
                    id="select-menu"
                    width="${this.calculateMenuSize()}"
                    identifier="${this._sdsSelect.pagination ? 'pagination' : nothing}"
                >
                    <slot @slotchange="${this._sdsSelect.onContentChange}"></slot>
                    ${noData
            ? html `<sds-cm-select-item
                              disabled
                              heading
                              name="${translate('sds.no_data')}"
                              role="option"
                              aria-disabled="true"
                          ></sds-cm-select-item>`
            : nothing}
                </sds-context-menu>
            </div>
        `;
    }
    willUpdate(_changedProperties) {
        if (_changedProperties.has('selectInputValue')) {
            this._sdsSelect.updateMenuContent();
        }
        this._wasLoading =
            this._sdsSelect.selectItems.length > 0 &&
                this._sdsSelect.selectItems.every(item => item instanceof SdsContextMenuSkeletonItem);
    }
    dispatchClearEvent(identifier) {
        this._sdsSelect.dispatchEvent(new CustomEvent(SdsSelect.CLEARED_EVENT, {
            detail: {
                identifier: identifier,
            },
            bubbles: false,
            composed: false,
        }));
    }
    resetScroll() {
        this._sdsSelect.contextMenu.getMenu().scrollTop = 0;
    }
    resetValue() {
        this._sdsSelect.selectInputValue = '';
        this._sdsSelect.inputComponent.value = '';
        this._sdsSelect.selectedItemIdentifier = '';
        this._sdsSelect.filter = '';
        this._sdsSelect.dispatchInputEvent();
    }
    hideMenu(focusInput = true) {
        var _a;
        if ((_a = this._sdsSelect.contextMenu) === null || _a === void 0 ? void 0 : _a.opened) {
            this._sdsSelect.contextMenu.hide(focusInput);
            this._sdsSelect.inputComponent.opened = false;
            // Clear ariaActiveDescendantElement when menu closes
            this._sdsSelect.clearAriaActiveDescendant();
            // Force a re-render to update the aria-expanded binding
            this._sdsSelect.requestUpdate();
            this.resetInputValue();
            // Set focused property for styling/state (separate from DOM focus)
            this._sdsSelect.focused = focusInput;
            this._sdsSelect.filter = '';
            this._sdsSelect.removeEventListeners();
            cleanMenuItemsFocus(this._sdsSelect.contextMenu);
            this._sdsSelect.dispatchMenuCloseEvent();
        }
    }
    resetInputValue() {
        this._sdsSelect.selectInputValue = '';
        this._sdsSelect.inputComponent.value = '';
        if (this._sdsSelect.selectedItemIdentifier !== '') {
            this._sdsSelect.selectItemStateHandler(item => {
                if (item.identifier === this._sdsSelect.selectedItemIdentifier) {
                    this._sdsSelect.selectInputValue = item.name;
                    this._sdsSelect.inputComponent.value = item.name;
                }
            });
        }
    }
    firstUpdated() {
        return;
    }
}
